import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories, categoryMap } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [currentSlug, setCurrentSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setCurrentSlug(slug);
    }
  }, [slug]);

  const currentCategory = categoryMap[currentSlug] || categories[0];
  const categoryComics = getComicsByCategory(currentSlug);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `色漫屋${currentCategory.name}漫画 - 在线免费阅读`,
    description: `色漫屋${currentCategory.name}漫画专区，${currentCategory.description}，汇集优质${currentCategory.name}类漫画作品，免费在线阅读。`,
    url: `https://baixinsh.com.cn/category/${currentSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '色漫屋',
      url: 'https://baixinsh.com.cn',
    },
  };

  return (
    <>
      <SEOHead
        title={`色漫屋${currentCategory.name}漫画 - ${currentCategory.description} | 在线免费阅读`}
        description={`色漫屋${currentCategory.name}漫画专区，${currentCategory.description}，汇集优质${currentCategory.name}类漫画作品，更新快速，免费在线阅读与下载。色漫屋漫画分类清晰，追漫首选。`}
        keywords={`色漫屋${currentCategory.name}漫画,${currentCategory.name}漫画在线,${currentCategory.name}漫画免费,色漫屋漫画分类`}
        canonicalUrl={`https://baixinsh.com.cn/category/${currentSlug}`}
        ogTitle={`色漫屋${currentCategory.name}漫画 - ${currentCategory.description}`}
        ogDescription={`色漫屋${currentCategory.name}漫画专区，${currentCategory.description}，汇集优质${currentCategory.name}类漫画作品。`}
        structuredData={categorySchema}
      />
      <article>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${currentCategory.name}漫画` },
            ]}
          />

          <div className="mt-6 mb-8">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 flex items-center gap-3">
              <i className={`${currentCategory.icon} text-primary-500`}></i>
              {currentCategory.name}漫画
            </h1>
            <p className="text-sm text-foreground-600 mt-2">{currentCategory.description}</p>
          </div>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categoryComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4 block"></i>
              <p className="text-foreground-500">该分类暂无漫画作品，敬请期待！</p>
              <Link
                to="/"
                className="inline-block mt-4 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </article>
    </>
  );
}